/**
 * Perena API Service
 * Real implementation for perena operations
 */

import { IPerenaApiService } from "./api";
import {
  GetPriceResponse,
  GetPriceApiResponse,
  GetApyResponse,
  QuoteResponse,
  GetStatsResponse,
  MintResponse,
  BurnResponse,
  MintQuoteRequest,
  BurnQuoteRequest,
  MintRequest,
  BurnRequest,
  PortfolioRequest,
  PortfolioResponse,
  WeeklyYieldResponse,
  PERENA_CONFIG,
} from "./types";
import { http } from "@/lib/http";

export class PerenaService implements IPerenaApiService {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    this.baseUrl = PERENA_CONFIG.baseUrl;
    this.timeout = PERENA_CONFIG.timeout;
  }

  // Token management helpers
  private getStoredToken(): string | null {
    let token = localStorage.getItem("auth_token");

    const expiry = localStorage.getItem("auth_expiry");
    if (token && expiry) {
      const expiryTime = parseInt(expiry) * 1000;
      if (Date.now() > expiryTime) {
        // Token expired, clear it
        this.removeStoredTokens();
        return null;
      }
    }

    if (!token) {
      token = sessionStorage.getItem("auth_token");
    }

    return token;
  }

  private removeStoredTokens(): void {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_expiry");
    sessionStorage.removeItem("auth_token");
  }

  // Get current USD* price
  async getPrice(): Promise<GetPriceResponse> {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      const response = await http.request({
        url: `${this.baseUrl}/perena/price`,
        method: "GET",
        headers,
        timeout: this.timeout,
      });

      const data = response.data?.data ?? response.data;
      return {
        success: true,
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        data: {
          price: 0,
          token: "",
          timestamp: "",
        },
        error:
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch price",
      };
    }
  }

  // Get USDStar price (with timestamp parameter)
  async getPriceApi(time?: string): Promise<GetPriceApiResponse> {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      const url = time
        ? `${this.baseUrl}/perena/price-api?time=${encodeURIComponent(time)}`
        : `${this.baseUrl}/price-api`;

      const response = await http.request({
        url,
        method: "GET",
        headers,
        timeout: this.timeout,
      });

      const data = response.data?.data ?? response.data;
      return {
        success: true,
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        data: {
          price: 0,
          timestamp: "",
        },
        error:
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch price",
      };
    }
  }

  // Get USDStar APY
  async getApy(time?: string): Promise<GetApyResponse> {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      const url = time
        ? `${this.baseUrl}/perena/apy?time=${encodeURIComponent(time)}`
        : `${this.baseUrl}/perena/apy`;

      const response = await http.request({
        url,
        method: "GET",
        headers,
        timeout: this.timeout,
      });

      const data = response.data?.data ?? response.data;
      return {
        success: true,
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        data: {
          period: "",
          apy: 0,
          timestamp: "",
        },
        error:
          error.response?.data?.error || error.message || "Failed to fetch APY",
      };
    }
  }

  // Get quote for minting USD*
  async getMintQuote(request: MintQuoteRequest): Promise<QuoteResponse> {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      const response = await http.request({
        url: `${this.baseUrl}/perena/quote/mint`,
        method: "POST",
        headers,
        data: request,
        timeout: this.timeout,
      });

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      return {
        success: false,
        data: {
          inputAmount: 0,
          outputAmount: 0,
          inputToken: "",
          outputToken: "",
          exchangeRate: 0,
          fee: 0,
        },
        error:
          error.response?.data?.error ||
          error.message ||
          "Failed to get mint quote",
      };
    }
  }

  // Get quote for burning USD*
  async getBurnQuote(request: BurnQuoteRequest): Promise<QuoteResponse> {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      const response = await http.request({
        url: `${this.baseUrl}/perena/quote/burn`,
        method: "POST",
        headers,
        data: request,
        timeout: this.timeout,
      });

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      return {
        success: false,
        data: {
          inputAmount: 0,
          outputAmount: 0,
          inputToken: "",
          outputToken: "",
          exchangeRate: 0,
          fee: 0,
        },
        error:
          error.response?.data?.error ||
          error.message ||
          "Failed to get burn quote",
      };
    }
  }

  // Get protocol statistics
  async getStats(): Promise<GetStatsResponse> {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      const response = await http.request({
        url: `${this.baseUrl}/perena/stats`,
        method: "GET",
        headers,
        timeout: this.timeout,
      });

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      return {
        success: false,
        data: {
          usdStarPrice: 0,
          environment: "",
          cluster: "",
          rpcUrl: "",
        },
        error:
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch stats",
      };
    }
  }

  // Mint USD* from USDC
  async mint(request: MintRequest): Promise<MintResponse> {
    const token = this.getStoredToken();
    if (!token) {
      return {
        success: false,
        data: {
          hash: "",
          expectedOutput: 0,
          inputAmount: 0,
          inputToken: "",
          outputToken: "",
        },
        error: "Authentication required",
      };
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const response = await http.request({
        url: `${this.baseUrl}/perena/mint`,
        method: "POST",
        headers,
        data: request,
        timeout: this.timeout,
      });

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      return {
        success: false,
        data: {
          hash: "",
          expectedOutput: 0,
          inputAmount: 0,
          inputToken: "",
          outputToken: "",
        },
        error:
          error.response?.data?.error || error.message || "Failed to mint USD*",
      };
    }
  }

  // Burn USD* for USDC
  async burn(request: BurnRequest): Promise<BurnResponse> {
    const token = this.getStoredToken();
    if (!token) {
      return {
        success: false,
        data: {
          hash: "",
          expectedOutput: 0,
          inputAmount: 0,
          inputToken: "",
          outputToken: "",
        },
        error: "Authentication required",
      };
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const response = await http.request({
        url: `${this.baseUrl}/perena/burn`,
        method: "POST",
        headers,
        data: request,
        timeout: this.timeout,
      });

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      return {
        success: false,
        data: {
          hash: "",
          expectedOutput: 0,
          inputAmount: 0,
          inputToken: "",
          outputToken: "",
        },
        error:
          error.response?.data?.error || error.message || "Failed to burn USD*",
      };
    }
  }

  // Get portfolio data for a wallet
  async getPortfolio(request: PortfolioRequest): Promise<PortfolioResponse> {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      const response = await http.request({
        url: `${this.baseUrl}/perena/portfolio`,
        method: "POST",
        headers,
        data: request,
        timeout: this.timeout,
      });

      const data = response.data?.data ?? response.data;
      return {
        success: true,
        data: {
          earnings: data.sourceData?.earnings ?? 0,
          usdStarBalance: data.usdStarBalance ?? 0,
          currentPrice: data.currentPrice ?? 0,
          netValue: data.netValue ?? 0,
          netValueFormatted: data.netValueFormatted ?? "$0.00",
        },
      };
    } catch (error: any) {
      return {
        success: false,
        data: {
          earnings: 0,
          usdStarBalance: 0,
          currentPrice: 0,
          netValue: 0,
          netValueFormatted: "$0.00",
        },
        error:
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch portfolio",
      };
    }
  }

  // Get weekly yield for a wallet
  async getWeeklyYield(walletAddress: string): Promise<WeeklyYieldResponse> {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      const response = await http.request({
        url: `${this.baseUrl}/perena/yield/weekly/${walletAddress}`,
        method: "GET",
        headers,
        timeout: this.timeout,
      });

      const data = response.data?.data ?? response.data;
      const normalizedYieldAmount =
        typeof data === "number"
          ? data
          : (data?.earnings ??
              data?.yieldAmount ??
              data?.weeklyUsdYield ??
              data?.yield ??
              0);

      return {
        success: true,
        data: {
          yieldAmount: normalizedYieldAmount,
          earnings:
            typeof data === "number"
              ? normalizedYieldAmount
              : (data?.earnings ?? normalizedYieldAmount),
          earningsFormatted:
            typeof data === "object" ? data?.earningsFormatted : undefined,
          currentBalance:
            typeof data === "object" ? data?.currentBalance : undefined,
          currentDay: typeof data === "object" ? data?.currentDay : undefined,
          weekStart: typeof data === "object" ? data?.weekStart : undefined,
          weekEnd: typeof data === "object" ? data?.weekEnd : undefined,
          priceAtWeekStart:
            typeof data === "object" ? data?.priceAtWeekStart : undefined,
          currentPrice: typeof data === "object" ? data?.currentPrice : undefined,
          priceChange: typeof data === "object" ? data?.priceChange : undefined,
          priceChangeFormatted:
            typeof data === "object" ? data?.priceChangeFormatted : undefined,
        },
      };
    } catch (error: any) {
      return {
        success: false,
        data: {
          yieldAmount: 0,
        },
        error:
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch weekly yield",
      };
    }
  }
}
